"use client";
import React, { useState, useEffect, lazy, Suspense } from 'react';
import PriceInfoBlock from './PriceInfoBlock';
import styles from './PriceInfoWithChart.module.scss';

// Lazy load the chart component for code splitting
const PriceChartBlock = lazy(() => import('./PriceChartBlock'));

// Loading skeleton for chart component
const ChartSkeleton: React.FC = () => (
  <div className={styles.priceChartBlockWrapper}>
    <div className={styles.priceChartBlockChart}>
      <div className={styles.chartSkeletonChart}></div>
    </div>
    <div className={styles.priceChartBlockPeriods}>
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <span className={styles.priceChartBlockPeriodSeparator} />
      <div className={styles.chartSkeletonPeriodBtn}></div>
      <div className={styles.chartSkeletonPeriodBtn}></div>
    </div>
    <h2 className={styles.priceChartBlockLabel}>
      <div className={styles.chartSkeletonLabel} style={{ width: '200px', height: '12px' }}></div>
    </h2>
  </div>
);

interface PriceInfoWithChartProps {
  token: {
    name: string;
    symbol: string;
    mintAddress: string;
    price?: number;
    priceChange?: number;
  };
}

// Helper to detect mobile device and viewport width
function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 640;
}

function getIntervals(): Record<string, string> {
  if (typeof window !== 'undefined' && isMobileViewport()) {
    return {
      '1H': '1m',
      '1D': '30m',
      '1W': '4H',
      '1M': '1D',
      '1Y': '1W',
    } as Record<string, string>;
  }
  return {
    '1H': '1m',
    '1D': '15m',
    '1W': '1H',
    '1M': '12H',
    '1Y': '3D',
  } as Record<string, string>;
}

function getPeriodRange(period: string): { from: number; to: number; interval: string } {
  const now = Math.floor(Date.now() / 1000);
  const intervals = getIntervals();
  let from = now;
  const interval = intervals[period as keyof typeof intervals] || '1H';
  switch (period) {
    case '1H': from = now - 3600; break;
    case '1D': from = now - 86400; break;
    case '1W': from = now - 7 * 86400; break;
    case '1M': from = now - 30 * 86400; break;
    case '1Y': from = now - 365 * 86400; break;
    default: from = now - 7 * 86400;
  }
  return { from, to: now, interval };
}

const SETTINGS_KEY = "sol_web_token_overview_settings";

function getOverviewSettings() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function setOverviewSettings(newSettings: Record<string, any>) {
  if (typeof window !== "undefined") {
    const current = getOverviewSettings();
    const updated = { ...current, ...newSettings };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  }
}

const PriceInfoWithChart: React.FC<PriceInfoWithChartProps> = ({ token }) => {
  // Hydration-safe: always use deterministic default, update from localStorage after mount
  const [mounted, setMounted] = useState(false);
  const [period, setPeriodState] = useState('1W');
  const [chartType, setChartTypeState] = useState<'candlestick' | 'area'>('candlestick');
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [preloadChart, setPreloadChart] = useState(false);
  const [chartLoaded, setChartLoaded] = useState(false);

  // On mount, update from localStorage if present
  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const settings = getOverviewSettings();
      if (settings.period && ['1H','1D','1W','1M','1Y'].includes(settings.period)) {
        setPeriodState(settings.period);
      }
      if (settings.chartType && ['candlestick','area'].includes(settings.chartType)) {
        setChartTypeState(settings.chartType);
      }
    }
    
    // Show chart after a short delay for progressive loading
    const timer = setTimeout(() => {
      setShowChart(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Preload chart on hover
  const handleChartAreaHover = () => {
    if (!preloadChart) {
      setPreloadChart(true);
    }
  };

  // Set chart as loaded when data is available and component is mounted
  useEffect(() => {
    if (showChart && chartData.length > 0 && mounted) {
      const timer = setTimeout(() => {
        setChartLoaded(true);
      }, 100); // Small delay to ensure smooth transition
      
      return () => clearTimeout(timer);
    }
  }, [showChart, chartData, mounted]);

  // Wrap setters to update localStorage
  const setPeriod = (p: string) => {
    setPeriodState(p);
    setOverviewSettings({ period: p });
  };
  const setChartType = (t: 'candlestick' | 'area') => {
    setChartTypeState(t);
    setOverviewSettings({ chartType: t });
  };

  const fetchChartData = async () => {
    if (!token?.mintAddress) return;
    
    setLoading(true);
    try {
      const { from, to, interval } = getPeriodRange(period);
      const url = `/tkn_lnd_pg_api/tokenChart/?baseAddress=${token.mintAddress}&currency=usd&timeFrom=${from}&timeTo=${to}&interval=${interval}&period=${period}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch chart data');
      
      const data = await response.json();
      
      // Handle the response structure: { items: Array, interval: string, baseAddress: string, ... }
      if (data && data.items && Array.isArray(data.items)) {
        setChartData(data.items);
      } else {
        setChartData([]);
      }
    } catch  {
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mounted) return;
    fetchChartData();
  }, [token.mintAddress, period, mounted]);

  // Always show skeleton during initial mount to prevent layout shift
  if (!mounted) {
    return (
      <div className={styles.priceInfoWithChartWrapper}>
        <PriceInfoBlock
          price={token.price}
          priceChange={token.priceChange}
        />
        <div className={styles.chartArea}>
          <ChartSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceInfoWithChartWrapper}>
      <PriceInfoBlock
        price={token.price}
        priceChange={token.priceChange}
      />
      
      {/* Chart area with hover preload */}
      <div 
        className={styles.chartArea}
        onMouseEnter={handleChartAreaHover}
        onTouchStart={handleChartAreaHover}
      >
        {/* Always show skeleton until chart is fully loaded */}
        {chartData.length > 0 && chartLoaded && showChart ? (
          <Suspense fallback={<ChartSkeleton />}>
            <PriceChartBlock
              data={chartData}
              loading={loading}
              period={period}
              setPeriod={setPeriod}
              tokenName={token.name}
              chartType={chartType}
              setChartType={setChartType}
            />
          </Suspense>
        ) : (
          <ChartSkeleton />
        )}
      </div>
    </div>
  );
};

export default PriceInfoWithChart;
